import { AddCircle, People } from '@mui/icons-material';
import { Box, Button, Chip, Divider, SxProps, Typography } from '@mui/material';
import React, { useContext, useState } from 'react';
import { AppContext } from '../../AppContext';
import { Roles } from '../../enums/RoleEnums';
import {
  GetRolesDocument,
  IGetRolesQuery,
  IRole,
  IUser,
  useGetRolesQuery,
  useRemoveRoleFromUserMutation
} from '../../generated/graphql';
import { Colors } from '../../utils/theme';
import { coloring, makeFirstLetterCapital } from './Users/UsersList';
import { PeopleAlt } from '@mui/icons-material';
import { Actions } from '../../enums/ActionEnums';
import OverviewToolbar from '../Common/Layout/OverviewToolbar';
import OverviewTable from '../Common/Tables/OverviewTable';
import AddUserToGroup from './AddUserToGroup';

const adminStyles: SxProps = {
  color: Colors.main,
  fontSize: 14,
  background: '#FCF2DD',
  '&:active, &:focus': {
    background: '#FCF2DD'
  }
};

const invoiceEditorStyles: SxProps = {
  color: Colors.main,
  fontSize: 14,
  background: '#E2E9EF',
  '&:active, &:focus': {
    background: '#E2E9EF'
  }
};

// const operatorStyles: SxProps = {
//   color: Colors.main,
//   fontSize: 14,
//   background: '#E8F2FE',
//   '&:active, &:focus': {
//     background: '#E8F2FE'
//   }
// };

const esg_operatorStyles: SxProps = {
  color: Colors.main,
  fontSize: 14,
  background: '#FDE9FD',
  '&:active, &:focus': {
    background: '#FDE9FD'
  }
};

const index_operatorStyles: SxProps = {
  color: Colors.main,
  fontSize: 14,
  background: '#E5F8F6',
  '&:active, &:focus': {
    background: '#E5F8F6'
  }
};

const marketing_operatorStyles: SxProps = {
  color: Colors.main,
  fontSize: 14,
  background: '#EDE5F8',
  '&:active, &:focus': {
    background: '#EDE5F8'
  }
};

// const adminDeleteIconStyles: SxProps = {
//   color: '#bf8104',
//   width: 28,
//   height: 28,
//   '&:hover': {
//     color: '#EBA61C'
//   }
// };

const operatorDeleteIconStyles: SxProps = {
  color: Colors.third,
  width: 28,
  height: 28,
  '&:hover': {
    color: Colors.main
  }
};

const esg_operatorDeleteIconStyles: SxProps = {
  color: Colors.pink,
  width: 28,
  height: 28,
  '&:hover': {
    color: '#9e199e'
  }
};

const index_operatorDeleteIconStyles: SxProps = {
  color: Colors.dark_green,
  width: 28,
  height: 28,
  '&:hover': {
    color: '#009e8e'
  }
};

const marketing_operatorDeleteIconStyles: SxProps = {
  color: Colors.purple,
  width: 28,
  height: 28,
  '&:hover': {
    color: '#8639ed'
  }
};

export type ValueType = IUser;

const UserGroups: React.FunctionComponent = () => {
  const { data, loading } = useGetRolesQuery();
  const [removeRoleFromUser] = useRemoveRoleFromUserMutation();
  const { dispatch } = useContext(AppContext);
  const [openAddUserForm, setOpenAddUserForm] = useState(false);
  const [selectedValueList, setSelectedValueList] = useState('');
  const [values, setValues] = useState<ValueType[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');

  const getChipStyles = (name: string) => {
    switch (name) {
      case Roles.Admin:
        return adminStyles;
      case Roles.InvoiceEditor:
      case Roles.Operator:
        return invoiceEditorStyles;
      case Roles.EsgOperator:
        return esg_operatorStyles;
      case Roles.IndexOperator:
        return index_operatorStyles;
      case Roles.MarketingOperator:
        return marketing_operatorStyles;
      default:
        return '';
    }
  };

  const getChipDeleteIconStyles = (name: string) => {
    switch (name) {
      case Roles.Operator:
        return operatorDeleteIconStyles;
      case Roles.EsgOperator:
        return esg_operatorDeleteIconStyles;
      case Roles.IndexOperator:
        return index_operatorDeleteIconStyles;
      case Roles.MarketingOperator:
        return marketing_operatorDeleteIconStyles;
      default:
        return '';
    }
  };

  const renderUsers = ({ id, name, users }: IRole) => {
    const canDelete = name !== Roles.Admin && name !== Roles.InvoiceEditor;
    return (
      <Box sx={{ display: 'flex' }}>
        {users.map((user, index) => (
          <Box key={index} sx={{ margin: '10px', alignItems: 'center' }}>
            {canDelete ? (
              <Chip
                id={`user-${user?.username}-username`}
                data-testid={`user-${user?.username}-username`}
                label={user?.username}
                onDelete={() => handleRemoveUser(user as IUser, id, name)}
                sx={{
                  ...getChipStyles(name),
                  '.MuiChip-deleteIcon': getChipDeleteIconStyles(name)
                }}
              />
            ) : (
              <Chip
                id={`user-${user?.username}-username`}
                data-testid={`user-${user?.username}-username`}
                label={user?.username}
                sx={{
                  ...getChipStyles(name),
                  '.MuiChip-deleteIcon': getChipDeleteIconStyles(name)
                }}
              />
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const renderAddUser = ({ id, name, users }: IRole) => {
    const disable = name === Roles.Admin || name === Roles.InvoiceEditor;
    return (
      <Button
        id={`${id}-${name}-add-user-button`}
        data-testid={`${id}-${name}-add-user-button`}
        onClick={() => handleOpenAddUserToGroup(id, name, users as ValueType[])}
        disabled={disable}
      >
        <AddCircle
          sx={{ color: disable ? Colors.gray : Colors.skyBlue, height: '42px', width: '42px' }}
        />
      </Button>
    );
  };

  const renderGroupName = ({ name }: IRole) => {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))' }}>
        <Box sx={{ height: '50px', gridColumn: 'span 1' }}>
          <Divider orientation="vertical" sx={{ background: coloring(name), width: '3px' }} />
        </Box>
        <Box sx={{ marginTop: '10px', gridColumn: 'span 11' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <PeopleAlt
              sx={{
                color: coloring(name),
                marginRight: '10px'
              }}
            />
            <Typography
              id={`${name}-group`}
              data-testid={`${name}-group`}
              sx={{
                fontFamily: 'Overpass',
                fontSize: '14px',
                color: coloring(name),
                marginTop: '5px',
                marginRight: '25px'
              }}
            >
              {makeFirstLetterCapital(name.replace('_', ' '))}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const columns = [
    { title: 'User Group', width: 350, sorting: false, render: renderGroupName },
    { title: 'Users', width: 1232, sorting: false, render: renderUsers },
    { title: 'Add User', width: 140, sorting: false, render: renderAddUser }
  ];

  const handleRemoveUser = (user: IUser, roleId: string, roleName: string) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to remove ${user.username} from ${roleName.replace(
          '_',
          ' '
        )} group?`,
        agreeAction: removeUserConfirmed,
        params: [user, roleId, roleName]
      }
    });
  };

  const removeUserConfirmed = (params: string[]) => {
    const user = params[0] as unknown as IUser;
    const userId = user.id;
    const roleId = params[1];
    const roleName = params[2];
    dispatch({ type: Actions.HideConfirmation });
    removeRoleFromUser({
      variables: {
        input: {
          user_id: parseInt(userId),
          role_id: parseInt(roleId)
        }
      },
      update(cache) {
        const groupsQuery = cache.readQuery({
          query: GetRolesDocument
        }) as IGetRolesQuery;
        if (groupsQuery.result) {
          const data = groupsQuery.result.map((gr) => {
            if (gr?.id === roleId) {
              return { ...gr, users: gr.users.filter((u) => u?.id !== userId) };
            } else {
              return gr;
            }
          });
          cache.writeQuery({
            query: GetRolesDocument,
            data: { result: data }
          });
        }

        dispatch({
          type: Actions.ShowMessage,
          payload: {
            text: `User ${user.username} removed from ${roleName} group`,
            severity: 'success'
          }
        });
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      })
    );
  };

  const handleOpenAddUserToGroup = (selectedRoleId: string, name: string, values: ValueType[]) => {
    setSelectedValueList(name);
    setValues(values);
    setSelectedRoleId(selectedRoleId);
    setOpenAddUserForm(true);
  };

  const handleCloseAddUserToGroup = () => {
    setSelectedValueList('');
    setValues([]);
    setSelectedRoleId('');
    setOpenAddUserForm(false);
  };

  return (
    <Box className="overviewPage">
      {/* <div style={{ width: '100%', paddingBottom: '45px', zIndex: 2 }}> */}
      <Box sx={{ width: '100%', zIndex: 2 }}>
        <OverviewToolbar>
          <People sx={{ marginRight: '7px' }} /> User Groups
        </OverviewToolbar>
      </Box>
      <Box>
        <OverviewTable
          columns={columns}
          data={data?.result || []}
          loading={loading}
          handleDetails={() => true}
          handleRemove={() => true}
          pageSize={10}
          hideSearch={true}
        />
      </Box>
      {openAddUserForm && (
        <AddUserToGroup
          name={selectedValueList}
          values={values}
          id={selectedRoleId}
          handleClose={handleCloseAddUserToGroup}
        />
      )}
    </Box>
  );
};

export default UserGroups;
